/**
 * Masterclass funnel analytics.
 *
 * One place for every event in the /masterclass → /book → /checkout/return
 * funnel, so CTA clicks, checkout submits, and paid confirmations all carry
 * the same UTM attribution and the same naming.
 */
import { trackCTA, trackConversion, trackEvent } from "@/lib/tracking";
import { attributionProps } from "@/lib/utm";
import { OFFERS } from "@/content/offers";

// Sourced from the offer registry so a price change updates the funnel value.
export const MASTERCLASS_PRICE_ID = OFFERS.masterclass.priceId as string;
export const MASTERCLASS_PRICE_USD = OFFERS.masterclass.priceUsd as number;

const INTENT_KEY = "masterclass_intent";

interface MasterclassIntent {
  cta_id: string;
  ts: number;
}

type Props = Record<string, string | number | boolean | undefined>;

const base = (extra: Props = {}): Props => ({
  funnel: "masterclass",
  price_id: MASTERCLASS_PRICE_ID,
  value: MASTERCLASS_PRICE_USD,
  currency: "USD",
  ...attributionProps(),
  ...extra,
});

/** Page-level view event — fires once when /masterclass mounts. */
export function trackMasterclassView(props: Props = {}) {
  trackEvent("masterclass_view", base(props));
}

/**
 * A CTA click anywhere in the masterclass funnel. Clicks that head toward
 * checkout also stamp a session-scoped intent record so the eventual
 * confirmation can be attributed back to this button.
 */
export function trackMasterclassCTA(
  ctaId: string,
  props: Props & { intent?: boolean } = {},
) {
  const { intent, ...rest } = props;
  trackCTA(ctaId, base({ surface: "masterclass", ...rest }));
  if (intent) markMasterclassIntent(ctaId);
}

/** Records that the visitor set out to buy the masterclass. */
export function markMasterclassIntent(ctaId: string) {
  if (typeof window === "undefined") return;
  try {
    const payload: MasterclassIntent = { cta_id: ctaId, ts: Date.now() };
    window.sessionStorage.setItem(INTENT_KEY, JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

export function readMasterclassIntent(): MasterclassIntent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(INTENT_KEY);
    return raw ? (JSON.parse(raw) as MasterclassIntent) : null;
  } catch {
    return null;
  }
}

export function clearMasterclassIntent() {
  try {
    window.sessionStorage?.removeItem(INTENT_KEY);
  } catch {
    /* best-effort */
  }
}

/** Form submit — the buyer submitted their details to open secure checkout. */
export function trackMasterclassRegisterSubmit(props: Props = {}) {
  const intent = readMasterclassIntent();
  trackConversion(
    "masterclass_register_submit",
    base({
      origin_cta: intent?.cta_id,
      seconds_since_cta: intent ? Math.round((Date.now() - intent.ts) / 1000) : undefined,
      ...props,
    }),
  );
}

/** Successful confirmation — payment completed for the masterclass. */
export function trackMasterclassConfirmed(props: Props = {}) {
  const intent = readMasterclassIntent();
  trackConversion(
    "masterclass_conversion",
    base({
      origin_cta: intent?.cta_id,
      seconds_since_cta: intent ? Math.round((Date.now() - intent.ts) / 1000) : undefined,
      ...props,
    }),
  );
  clearMasterclassIntent();
}
